import { useQuery, useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { readsMeta } from '@signalvote/data';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type {
  CommitteeFactsDto,
  MessagingProfileDto,
  MessagingProfileQueueResponse,
  UpdateMessagingProfileRequest,
  AdvanceProvisioningRequest,
  SaveCommitteeFactsRequest,
} from '@signalvote/shared';

const BASE = '/api/messaging/profile';

/** The server's own sentence and code when it gave one, so the page shows why a save was refused. */
function refusal(fallback: string) {
  return (body: ApiErrorBody, response: Response) => failureFromResponse(response, body, body.error || fallback);
}

const messagingProfile = defineQuery({
  key: () => ['messagingProfile', 'detail'],
  reads: 'GET /api/messaging/profile',
  fetch: async (_: undefined, { signal, campaignId }): Promise<MessagingProfileDto | null> => {
    const response = await apiResponse(BASE, { signal, headers: campaignHeaders(campaignId) });
    if (response.status === 404) return null;
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
      throw refusal('Failed to load messaging profile')(body, response);
    }
    return response.json() as Promise<MessagingProfileDto>;
  },
});

/**
 * The selected campaign's messaging profile, or `null` when it has not been created
 * yet (the API returns 404). A null result is the "no profile yet" state the page
 * renders a Create button for — it is NOT an error.
 */
export function useMessagingProfile() {
  return messagingProfile(undefined);
}

/** What the selected campaign told us about its committee, prefilled where it hasn't, and where registration stands. */
export function useCommitteeFacts({ enabled = true, refetchOnMount }: { enabled?: boolean; refetchOnMount?: 'always' } = {}) {
  const { selectedCampaign } = useCampaignStore();

  // Raw, with its reads declared so a write set still refreshes it: the Express committee step
  // gates on a fetch after mount, and the save reads its token from this entry when it runs.
  return useQuery({
    queryKey: queryKeys.messagingProfile.committee(),
    queryFn: () => getApiClient().messagingProfile.getCommitteeFacts(),
    meta: readsMeta('GET /api/messaging/profile/committee'),
    enabled: enabled && !!selectedCampaign,
    refetchOnMount,
  });
}

/** Which form saved the committee facts: the facts themselves, or the signature box. */
export type CommitteeSaveDoor = 'facts' | 'signature';

/**
 * Saves the selected campaign's committee facts, with the attestation when the signer confirms.
 * The token is read from the cached facts when the save runs, so a caller that waits for one save
 * before starting the next never sends a stale one. Refusals are `ApiError`s whose body names the
 * `field`.
 */
export function useSaveCommitteeFacts(door: CommitteeSaveDoor) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.messagingProfile.committeeSave(door),
    mutationFn: (body: Omit<SaveCommitteeFactsRequest, 'expectedConcurrencyVersion'>): Promise<CommitteeFactsDto> =>
      getApiClient().messagingProfile.saveCommitteeFacts({
        ...body,
        expectedConcurrencyVersion:
          queryClient.getQueryData<CommitteeFactsDto>(queryKeys.messagingProfile.committee())?.concurrencyVersion ?? 0,
      }),
    // The saved token lands at once so a save right after this one sends it; the write set refreshes the rest.
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.messagingProfile.committee(), saved);
    },
  });
}

/**
 * The form behind this page's last successful committee save, or null when there was none, as after a
 * reload. The server keeps no record of why a signature came off; this is the web's own memory of it.
 */
export function useLastCommitteeSaveDoor(): CommitteeSaveDoor | null {
  const doors = useMutationState({
    filters: { mutationKey: queryKeys.messagingProfile.committeeSaves(), status: 'success' },
    select: (mutation) => mutation.options.mutationKey?.at(-1) as CommitteeSaveDoor,
  });
  return doors.at(-1) ?? null;
}

/** Every campaign's registration, waiting sends first in send order (the API sorts; do not re-sort). */
const messagingProfiles = defineQuery({
  key: () => ['messagingProfiles', 'list'],
  reads: 'GET /api/messaging/profiles',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<MessagingProfileQueueResponse>('/api/messaging/profiles', {
      signal,
      headers: campaignHeaders(campaignId),
      error: refusal('Failed to load messaging profiles'),
    }),
});

export function useMessagingProfiles() {
  return messagingProfiles(undefined);
}

/** Creates the profile (NotStarted) for the selected campaign. */
export const useCreateMessagingProfile = defineWrite({
  send: (): Promise<MessagingProfileDto> =>
    apiJson<MessagingProfileDto>(BASE, { method: 'POST', error: refusal('Failed to create messaging profile') }),
});

/** Records the Campaign Verify pass + captures the use-case fields (partial update). */
export const useUpdateMessagingProfile = defineWrite({
  send: (data: UpdateMessagingProfileRequest): Promise<MessagingProfileDto> =>
    apiJson<MessagingProfileDto>(BASE, { method: 'PATCH', json: data, error: refusal('Failed to update messaging profile') }),
});

/** Advances the provisioning spine by one hand-driven edge. */
export const useAdvanceProvisioning = defineWrite({
  send: (data: AdvanceProvisioningRequest): Promise<MessagingProfileDto> =>
    apiJson<MessagingProfileDto>(`${BASE}/advance`, { method: 'POST', json: data, error: refusal('Failed to advance provisioning') }),
});
